import type { v2, ControlTypeFunction } from '@sap/ux-specification-types';
import { ControlType } from '@sap/ux-specification-types';
import { syncRule, buildControlIdFromParent, validity, convertSectionId } from '../../../common/decoration';
import { addPatternForBindingChangeOfEnumOP } from '../../../common';
import type { ButtonType } from 'sap/m/library';
import type { URI } from 'sap/ui/core/library';

/**
 * Create stable ID for Object Page Form Action button.
 *
 * @param {string} baseId Base Id of the control
 * @param {string[]} idBreadcrumbs Array of breadcrumbs used to build the ID
 * @param {string[]} breadcrumbs Array of breadcrumbs representing the navigation path
 * @param {ControlTypeFunction} controlType Function to determine the control type
 * @param {string} facetID title from the app schema (comprising for instance the facet ID)
 * @returns {string} stable ID of Object Page Form Action button
 */
const getFormActionButtonId = (
    baseId: string,
    idBreadcrumbs: string[],
    breadcrumbs: string[],
    controlType: ControlTypeFunction,
    facetID: string
): string => {
    if (controlType() === ControlType.Button) {
        const entity = breadcrumbs[breadcrumbs.length - 1];
        const section = convertSectionId(breadcrumbs[1], facetID);
        return `${baseId}action::${entity}::${section}::FormAction`;
    }
    return buildControlIdFromParent(baseId, idBreadcrumbs);
};

const flexRuleForFormAction = {
    controlId: getFormActionButtonId,
    controlType: (): ControlType => ControlType.Button
};

export class FormAction implements v2.FormAction {
    @syncRule({
        flex: flexRuleForFormAction,
        generate: addPatternForBindingChangeOfEnumOP
    })
    visible: boolean;

    @syncRule({
        flex: flexRuleForFormAction
    })
    tooltip?: string;

    @syncRule({
        flex: flexRuleForFormAction
    })
    icon?: URI;

    @syncRule({
        flex: flexRuleForFormAction
    })
    activeIcon?: URI;

    @syncRule({
        flex: flexRuleForFormAction
    })
    type?: ButtonType;
}

export class FieldForAction implements v2.FormAction {
    @syncRule({
        flex: flexRuleForFormAction,
        generate: addPatternForBindingChangeOfEnumOP
    })
    visible: boolean;

    @syncRule({
        flex: flexRuleForFormAction
    })
    tooltip?: string;

    @syncRule({
        flex: flexRuleForFormAction
    })
    icon?: URI;

    @syncRule({
        flex: flexRuleForFormAction
    })
    activeIcon?: URI;

    @syncRule({
        flex: flexRuleForFormAction
    })
    @validity({
        since: '1.73.0'
    })
    type?: ButtonType;
}
